// The emoji picker's pure half — port of the logic inside ios/Sources/Views/EmojiPickerSheet.swift
// (recents first, the whole webapp-derived catalogue, search by shortcode, `remember`), plus the
// web-only parts the Apple sheet has no need for: the webapp's category order, keyboard movement
// over a sectioned grid, and inserting at a caret.
//
// The catalogue is GENERATED (scripts/gen-emoji.mjs → generated/emojiCatalog.json) from the same
// webapp chunk as emojiCharacters.json; every glyph is that table's, so a chip, a preview and the
// picker can never draw the same name differently. Skin-tone variants (`+1_light_skin_tone`) come
// from the catalogue's `variants` — the Apple table does not carry them.
import catalog from './generated/emojiCatalog.json';
import { EmojiCharacters } from './emojiCharacters';

export interface CatalogEmoji {
  /** The primary shortcode — what a pick emits (the wire wants names, never glyphs). */
  name: string;
  /** Every shortcode the webapp knows it by (primary first) — what search matches. */
  names: string[];
  char: string;
}

export interface CatalogCategory {
  /** The webapp's category id: smileys-emotion, people-body, animals-nature, … flags. */
  id: string;
  emoji: CatalogEmoji[];
}

const raw = catalog as { categories: Array<{ id: string; emoji: string[] }>; variants: Record<string, string> };

let built: CatalogCategory[] | null = null;
let flat: CatalogEmoji[] | null = null;

/** The categories in the webapp's own order (built once, on first use). */
function categories(): CatalogCategory[] {
  if (!built) {
    built = raw.categories.map((c) => ({
      id: c.id,
      emoji: c.emoji.flatMap((entry) => {
        const names = entry.split(' ');
        const char = EmojiCharacters.character(names[0]);
        return char ? [{ name: names[0], names, char }] : [];
      }),
    }));
    flat = built.flatMap((c) => c.emoji);
  }
  return built;
}

function all(): CatalogEmoji[] {
  categories();
  return flat!;
}

/** The glyph for a shortcode: the Apple table first, then the skin-tone variants. */
function glyph(name: string): string | null {
  return EmojiCharacters.character(name) ?? (Object.prototype.hasOwnProperty.call(raw.variants, name) ? raw.variants[name] : null);
}

/** Apple's query rule: trimmed, lowercased; empty = no search. */
function normalizedQuery(query: string): string {
  return query.trim().toLowerCase();
}

/** 0 = a name IS the query, 1 = a name starts with it, 2 = a name contains it, -1 = no match. */
function band(names: readonly string[], needle: string): number {
  let best = -1;
  for (const n of names) {
    if (n === needle) return 0;
    if (n.startsWith(needle)) best = 1;
    else if (best < 0 && n.includes(needle)) best = 2;
  }
  return best;
}

/**
 * Search, Apple rule (`name.contains(query)`) over EVERY alias of an emoji, so "thumbsup" finds
 * :+1:. WEB: ranked in bands — the exact name, then names that START with the query, then the
 * rest (catalogue order within a band): on a 1,800-emoji grid "fire" must be :fire: before
 * :firefighter:, and "hear" :heart: before :ear_of_rice:.
 */
function search(query: string, emoji: readonly CatalogEmoji[] = all()): CatalogEmoji[] {
  const needle = normalizedQuery(query);
  if (!needle) return [...emoji];
  const bands: CatalogEmoji[][] = [[], [], []];
  for (const e of emoji) {
    const b = band(e.names, needle);
    if (b >= 0) bands[b].push(e);
  }
  return bands.flat();
}

/** Custom emoji names matching a query (same bands), sorted — a live `emoji_added` must not reshuffle. */
function searchCustom(query: string, names: readonly string[]): string[] {
  const needle = normalizedQuery(query);
  const sorted = [...names].sort();
  if (!needle) return sorted;
  const bands: string[][] = [[], [], []];
  for (const n of sorted) {
    const b = band([n], needle);
    if (b >= 0) bands[b].push(n);
  }
  return bands.flat();
}

export type SearchHit = { kind: 'unicode'; emoji: CatalogEmoji } | { kind: 'custom'; name: string };

/** One result list for the picker: unicode and the server's custom emoji interleaved BY BAND. */
function searchAll(query: string, customNames: readonly string[]): SearchHit[] {
  const needle = normalizedQuery(query);
  const bands: SearchHit[][] = [[], [], []];
  for (const e of search(needle)) bands[band(e.names, needle)].push({ kind: 'unicode', emoji: e });
  for (const name of searchCustom(needle, customNames)) bands[band([name], needle)].push({ kind: 'custom', name });
  return bands.flat();
}

/** How many recents `remember` keeps (Apple: `list.prefix(24)`). */
const recentsLimit = 24;

/** `EmojiPickerSheet.remember`: the pick moves to the front, deduped, capped. */
function remember(recentsRaw: string, name: string): string {
  const list = recentsRaw.split(',').filter((n) => n !== '' && n !== name);
  return [name, ...list].slice(0, recentsLimit).join(',');
}

/** `EmojiPickerSheet.recents`: the stored names this build can still draw (glyph or custom). */
function recents(recentsRaw: string, isCustom: (name: string) => boolean): string[] {
  return recentsRaw.split(',').filter((n) => n !== '' && (glyph(n) != null || isCustom(n)));
}

export interface Inserted {
  text: string;
  /** The caret after the insertion, in UTF-16 code units (a textarea's selectionStart). */
  caret: number;
}

/**
 * Puts a picked emoji at the caret, replacing any selection. A unicode emoji goes in as its GLYPH
 * (Apple: ":smile:" sitting literal in the box reads as broken); a CUSTOM one as `:name:`, which
 * the server only renders as an emoji when it stands apart from the words around it — so it gets
 * a space before when glued to text, and a space after (Apple's trailing space) unless one follows.
 */
function insertAtCaret(text: string, start: number, end: number, name: string, isCustom: boolean): Inserted {
  const from = Math.max(0, Math.min(start, text.length));
  const to = Math.max(from, Math.min(end, text.length));
  const before = text.slice(0, from);
  const after = text.slice(to);
  let insert: string;
  const char = isCustom ? null : glyph(name);
  if (char) insert = char;
  else {
    const lead = before !== '' && !/\s$/u.test(before) ? ' ' : '';
    const trail = after === '' || !/^\s/u.test(after) ? ' ' : '';
    insert = `${lead}:${name}:${trail}`;
  }
  return { text: before + insert + after, caret: from + insert.length };
}

// MARK: - Keyboard movement over a sectioned grid

/** A cell in the grid: which section, which item inside it. */
export interface GridPosition {
  section: number;
  index: number;
}

/**
 * Arrow-key movement over sections laid out `columns` wide (every section starts a new row).
 * Left/right walk the reading order across section ends; up/down keep the column, clamped to a
 * shorter row, and cross into the neighbouring section's nearest row. Empty sections are skipped.
 * Returns the same position when there is nowhere to go.
 */
function move(sizes: readonly number[], columns: number, at: GridPosition, key: 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown'): GridPosition {
  const cols = Math.max(1, columns);
  const nonEmpty = (s: number) => s >= 0 && s < sizes.length && sizes[s] > 0;
  if (!nonEmpty(at.section)) {
    const first = sizes.findIndex((n) => n > 0);
    return first < 0 ? at : { section: first, index: 0 };
  }
  const size = sizes[at.section];
  if (key === 'ArrowRight') {
    if (at.index + 1 < size) return { section: at.section, index: at.index + 1 };
    for (let s = at.section + 1; s < sizes.length; s++) if (nonEmpty(s)) return { section: s, index: 0 };
    return at;
  }
  if (key === 'ArrowLeft') {
    if (at.index > 0) return { section: at.section, index: at.index - 1 };
    for (let s = at.section - 1; s >= 0; s--) if (nonEmpty(s)) return { section: s, index: sizes[s] - 1 };
    return at;
  }
  const column = at.index % cols;
  if (key === 'ArrowDown') {
    const next = at.index + cols;
    if (next < size) return { section: at.section, index: next };
    // The last row of a section that has a row below its current one (a partial last row).
    const lastRowStart = Math.floor((size - 1) / cols) * cols;
    if (at.index < lastRowStart) return { section: at.section, index: size - 1 };
    for (let s = at.section + 1; s < sizes.length; s++) if (nonEmpty(s)) return { section: s, index: Math.min(column, sizes[s] - 1) };
    return at;
  }
  // ArrowUp
  if (at.index - cols >= 0) return { section: at.section, index: at.index - cols };
  for (let s = at.section - 1; s >= 0; s--) {
    if (!nonEmpty(s)) continue;
    const lastRowStart = Math.floor((sizes[s] - 1) / cols) * cols;
    return { section: s, index: Math.min(lastRowStart + column, sizes[s] - 1) };
  }
  return at;
}

export const EmojiPicker = {
  categories,
  all,
  glyph,
  search,
  searchCustom,
  searchAll,
  recentsLimit,
  remember,
  recents,
  insertAtCaret,
  move,
} as const;
